<template>
  <div class="field" :class="[horizontal && `field_horizontal`]">
    <div class="inner">
      <div v-if="$slots.label || $slots.description" class="header">
        <Label v-if="$slots.label" :required="required" class="label">
          <slot name="label" />

          <template v-if="$slots.info" #info>
            <slot name="info" />
          </template>
        </Label>

        <p
          v-if="$slots.description"
          :id="descriptionId"
          class="description break-words text-sm text-muted-foreground"
        >
          <slot name="description" />
        </p>
      </div>

      <div class="control">
        <slot />

        <p
          v-if="$slots.hint"
          :id="hintId"
          class="self-start text-[0.8125rem] leading-5 text-muted-foreground"
        >
          <slot name="hint" />
        </p>

        <p
          v-if="$slots.validationMessage"
          :id="validationMessageId"
          :role="effectiveStatus === 'error' ? 'alert' : undefined"
          :class="[
            `flex gap-1.5 text-[0.8125rem] font-medium leading-5`,
            effectiveStatus === 'error' && `text-red-700 dark:text-red-400`,
            effectiveStatus === 'warning' &&
              `text-yellow-700 dark:text-yellow-400`,
            effectiveStatus === 'success' &&
              `text-green-700 dark:text-green-400`,
            effectiveStatus === 'none' && `text-muted-foreground`,
          ]"
        >
          <span class="my-0.5 grid size-4 place-items-center">
            <slot name="validationMessageIcon">
              <ExclamationCircleIcon v-if="effectiveStatus === 'error'" />
              <ExclamationTriangleIcon
                v-else-if="effectiveStatus === 'warning'"
              />
              <CheckCircleIcon v-else-if="effectiveStatus === 'success'" />
            </slot>
          </span>

          <span
            :class="[effectiveStatus !== 'error' && `text-muted-foreground`]"
          >
            <slot name="validationMessage" />
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRef, useId, useSlots } from "vue";

import CheckCircleIcon from "@/icons/heroicons/check-circle-micro.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-micro.svg";

import { type ValidationStatus, provideFieldContext } from "./context";
import Label from "./Label.vue";

const props = defineProps<{
  horizontal?: boolean;
  required?: boolean;
  disabled?: boolean;
  validationStatus?: ValidationStatus;
}>();

const slots = useSlots();

const effectiveStatus = computed<ValidationStatus | undefined>(() => {
  if (props.validationStatus !== undefined) {
    return props.validationStatus;
  }
  return slots.validationMessage ? "error" : undefined;
});

const inputId = useId();
const descriptionId = `${inputId}-description`;
const hintId = `${inputId}-hint`;
const validationMessageId = `${inputId}-validation-message`;

provideFieldContext({
  inputId,
  descriptionId,
  hintId,
  validationMessageId,
  required: toRef(() => props.required ?? false),
  disabled: toRef(() => props.disabled ?? false),
  validationStatus: toRef(() => effectiveStatus.value),
});
</script>

<style scoped>
.field {
  display: block;
  container-type: inline-size;
  container-name: field;
}
.field_horizontal {
  /* 640px - (16px * 2) */
  @container field (width >= 608px) {
    .inner {
      display: grid;
      grid-template-areas: "header control";
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }

    .header {
      grid-area: header;
    }

    .header:not(:has(.description)) .label {
      margin-top: 7px;
    }

    .header:has(.description) ~ .control {
      margin-top: 6px;
    }

    .control {
      grid-area: control;
      align-items: end;
    }
  }
}

.inner {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.control {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}
</style>
